import { computed, makeAutoObservable, runInAction } from "mobx"
import { Currency, Locale, MeasurementUnit } from "../services/constantsRegistry/models";
import { getCurrencies, getLocales, getUnits } from "../services/constantsRegistry";
import { stores } from "./index";
import { SnackbarType } from "./common";
import { localize, toFormattedNumber } from "../common/libs";


class ConstantsStore {
    currencies: Currency[] = [];
    locales: Locale[] = [];
    units: MeasurementUnit[] = [];
    isReady: boolean = false;

    constructor() {
        makeAutoObservable(this);
    }


    async init() {
        const result = await Promise.all([
            getCurrencies(),
            getUnits(),
            getLocales()
        ]);
        runInAction(() => {
            let hasError = false;
            if (!result[0].err) {
                this.currencies = result[0].currencies;
            } else {
                hasError = true;
            }
            if (!result[1].err) {
                this.units = result[1].units;
            } else {
                hasError = true;
            }

            if (!result[2].err) {
                this.locales = result[2].locales;
            } else {
                hasError = true
            }

            if (!hasError) {
                this.isReady = true;
            } else {
                stores.generalStore.showSnackbar({
                    type: SnackbarType.error,
                    title: "Ошибка при получении данных",
                    description: `Не удалось получить валюты, единицы измерения или локали`
                })
            }
        });
    }


    @computed
    getDefaultUnit() {
        return this.units.length > 0 ? this.units[0].type : "Kilogram"
    }

    @computed
    getLocalisedUnitName(type: string) {
        const locale = stores.generalStore.locale;
        return this.units.find(item => item.type === type)?.name[locale.id] || "";
    }

    @computed
    getLocalisedCurrencyName(id: string) {
        const locale = stores.generalStore.locale;
        return this.currencies.find(item => item.id === id)?.currencyName[locale.id] || "";
    }

    @computed
    getLocalisedCurrencyShortName(id: string) {
        const locale = stores.generalStore.locale;
        return this.currencies.find(item => item.id === id)?.currencyShortName[locale.id] || "";
    }


    getUnitsFormatter(type: string) {
        const unit = this.units.find(item => item.type === type);
        return {
            toShow: (value: number) => {
                return (value / (10 ** unit.decimalPlaces)).toFixed(unit.decimalPlaces);
            },
            toSend: (value: number) => {
                return parseInt((value * (10 ** unit.decimalPlaces)).toFixed(0));
            },

            toShowNumber: (value: number) => {
                return parseFloat((value / (10 ** unit.decimalPlaces)).toFixed(unit.decimalPlaces));
            },
            toFloat: (v: string) => {
                const t = parseFloat(v.replace(/[^\d.-]/g, ''));
                if (!isNaN(t)) return parseFloat(t.toFixed(unit.decimalPlaces));
                return 0;
            },
            toStringFloat: (v: string) => {
                return v.replace(/[^\d.-]/g, '');
            },
            getUnit: () => {
                return unit;
            }
        }
    }

    getCurrencyFormatter(id: string) {
        const currency = this.currencies.find(item => item.id === id);
        return {
            toShow: (value: number, hideCurrency: boolean = false, hideDecimal: boolean = false) => {
                if(hideCurrency){
                    if(hideDecimal){
                        return `${(value / (10 ** currency.decimalPlaces)).toString()}`;
                    }
                    return `${(value / (10 ** currency.decimalPlaces)).toFixed(currency.decimalPlaces)}`;
                }else{
                    if(hideDecimal){
                        return `${(value / (10 ** currency.decimalPlaces)).toString()} ${localize(currency.currencyShortName)}`;
                    }
                    return `${(value / (10 ** currency.decimalPlaces)).toFixed(currency.decimalPlaces)} ${localize(currency.currencyShortName)}`;
                }
            },

            toShowFormatted: (value: number, hideCurrency: boolean = false) => {
                if(hideCurrency){
                    return toFormattedNumber(Math.round(value / (10 ** currency.decimalPlaces)));
                }else{
                    return `${toFormattedNumber(Math.round(value / (10 ** currency.decimalPlaces)))} ${localize(currency.currencyShortName)}`;
                }
            },
            toSend: (value: number) => {
                return parseInt((value * (10 ** currency.decimalPlaces)).toFixed(0));
            },
            toFloat: (v: string) => {
                const t = parseFloat(v.replace(/[^\d.-]/g, ''));
                if (!isNaN(t)) return parseFloat(t.toFixed(currency.decimalPlaces));
                return 0;
            },
            toStringFloat: (v: string) => {
                return v.replace(/[^\d.-]/g, '');
            }
        }
    }
}

export default ConstantsStore
